import React, { useEffect, useMemo, useState } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, ChevronRight, Download, LayoutGrid, Loader2, SatelliteDish, Text, X } from 'lucide-react';
import { TerminalType, UserTerminal } from '@/shared/types/userTerminalTypes';
import { useInfiniteAccountTerminals } from '@/domains/terminals/services/account-user-terminals';
import EmptyView from '@/shared/components/EmptyView';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from '@/shared/ui/card';
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead, TableFooter } from '@/shared/ui/table';
import { Badge } from "@/shared/ui/badge";
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Button } from '@/shared/ui/button';
import { exportToCsv } from '@/shared/utils/csv-export-utils';
import { TransferTerminalToAccountDialog } from '@/domains/terminals/dialogs/TransferTerminalToAccountDialog';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import TerminalNotesDialog from '@/domains/terminals/dialogs/TerminalNotesDialog';
import SearchBar from '@/shared/components/SearchBar';
import { DEBOUNCE_TIME } from '@/shared/utils/input-validator';
import { parseDateFromString } from '@/shared/utils/date-utils';
import { isSameMonth, isSameYear } from 'date-fns';
import { getServiceLineTypeBGColorClass, getServiceLineStatusBGColorClass, SERVICE_LINE_STATUS_VALUE, SERVICE_LINE_TYPE_VALUES } from '@/shared/utils/color-utils-service-lines';
import { getTerminalTypeTextColorClass, TERMINAL_TYPE_INFO } from '@/shared/utils/color-utils-terminals';
import { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import { TerminalsOverviewDialog } from '@/domains/global-dashboard/dialogs/TerminalsOverviewDialog';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';

export const refetchTerminalsData = (queryClient: QueryClient) => {
  void queryClient.resetQueries({ queryKey: ['user-terminals-infinite'] });
};

interface TerminalsTabProps {
  organizationOverviewData: OrganizationOverviewData | null | undefined;
  onManageServiceLine: (serviceLine: ServiceLine) => void;
}

export const TerminalsTab = ({ organizationOverviewData, onManageServiceLine }: TerminalsTabProps) => {
  const { hasPermission } = usePermissions();
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, DEBOUNCE_TIME);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const queryClient = useQueryClient();
  const {
    data: terminalsInfiniteData,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
  } = useInfiniteAccountTerminals(true, undefined, searchQuery);

  const terminals = useMemo(() => {
    const pages = terminalsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: UserTerminal[] = [];
    for (const page of pages) {
      for (const t of page.results ?? []) {
        if (!seen.has(t.userTerminalId)) {
          seen.add(t.userTerminalId);
          merged.push(t);
        }
      }
    }
    // Sort terminals by service line not null first
    merged.sort((a, b) => (a.serviceLine ? -1 : 1) - (b.serviceLine ? -1 : 1));
    return merged;
  }, [terminalsInfiniteData?.pages]);

  const totalCount = terminalsInfiniteData?.pages[0]?.totalCount ?? 0;
  const [terminalTypeFilter, setTerminalTypeFilter] = React.useState('all');
  const [isTransferTerminalToAccountDialogOpen, setIsTransferTerminalToAccountDialogOpen] = useState(false);
  const [isTerminalNotesDialogOpen, setIsTerminalNotesDialogOpen] = useState(false);
  const [isTerminalsOverviewDialogOpen, setIsTerminalsOverviewDialogOpen] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = React.useState<UserTerminal | null>(null);

  // Filter terminals based on current filters
  const filteredUserTerminals = useMemo(() => {
    if (!terminals || (terminals?.length ?? 0) === 0) return [];

    return terminals?.filter(terminal => {
      // Terminal type filter
      if (terminalTypeFilter !== 'all' && terminal.type !== terminalTypeFilter) return false;
      return true;
    });
  }, [terminals, terminalTypeFilter]);

  // Get current month summary data
  const currentMonthAccountSummaryData = useMemo(() => {
    return organizationOverviewData?.summary?.find(summary => isSameMonth(parseDateFromString(summary.startDate), new Date()) && isSameYear(parseDateFromString(summary.startDate), new Date())) ?? null;
  }, [organizationOverviewData]);

  const terminalsSummary = currentMonthAccountSummaryData?.terminalsSummary;

  const handleRefetchTerminals = () => {
    refetchTerminalsData(queryClient);
  };

  const handleServiceLineClick = (serviceLine: ServiceLine) => {
    onManageServiceLine(serviceLine);
  };

  const handleTransferTerminalToAccount = (terminal: UserTerminal) => {
    setSelectedTerminal(terminal)
    setIsTransferTerminalToAccountDialogOpen(true)
  }

  const handleTerminalNotes = (terminal: UserTerminal) => {
    setSelectedTerminal(terminal)
    setIsTerminalNotesDialogOpen(true)
  }

  // Handle export action
  const handleExportAction = () => {
    // If there is no data to export, show an alert
    if (!terminals || terminals.length === 0) {
      alert('No data to export');
      return;
    }

    // If the number of service lines is less than the total count, ask for confirmation
    if (terminals.length < totalCount) {
      if (confirm(`Only ${terminals.length} of ${totalCount} records will be exported. There are more records available on the next page(s). Please confirm to export?`)) {
        handleExport();
      }
      return;
    }

    // Export the data
    handleExport();
  }

  // Export the data
  const handleExport = () => {
    const csvData = terminals.map(terminal => {
      const terminalId = terminal.userTerminalId;
      const kitSerialNumber = terminal.kitSerialNumber;
      const dishSerialNumber = terminal.dishSerialNumber;
      const terminalType = terminal.type ? TERMINAL_TYPE_INFO[terminal.type].value : 'Not Set';
      const terminalStatus = terminal.serviceLine ? "Deployed" : "Not Deployed";
      const notes = terminal.notes || '-';
      const serviceLine = terminal.serviceLine;
      const serviceLineName = serviceLine?.serviceLineName || '-';
      const serviceLineNumber = serviceLine?.serviceLineNumber || '-';
      const serviceLineStatus = serviceLine?.status || '-';
      const serviceLineType = serviceLine?.type || '-';

      return {
        'Terminal ID': terminalId,
        'Kit Serial Number': kitSerialNumber,
        'Dish Serial Number': dishSerialNumber,
        'Terminal Type': terminalType,
        'Terminal Status': terminalStatus,
        'Notes': notes,
        'Service Line Name': serviceLineName,
        'Service Line Number': serviceLineNumber,
        'Service Line Status': serviceLineStatus,
        'Service Line Type': serviceLineType,
      };
    });

    exportToCsv(csvData, `account_terminals`);
  };

  const canManageTerminals = hasPermission(OrgType.TENANT, Feature.TERMINALS, Permission.MANAGE);

  return (
    <div className="space-y-4">
      {/* Terminals Header */}
      <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
        <div>
          <h1 className="text-3xl font-bold">Terminals</h1>
          <p className="text-lg">Manage hardware devices and terminals</p>
        </div>
        <div className="flex items-center gap-2">
          {organizationOverviewData && (
            <Button
              onClick={() => setIsTerminalsOverviewDialogOpen(true)}
              className="gap-2 bg-blue-500 hover:bg-blue-600"
              disabled={!organizationOverviewData}
            >
              <LayoutGrid className="h-4 w-4" />
              Overview
            </Button>
          )}
          {terminals?.length > 0 && !error && organizationOverviewData && (
            // Export Button
            <Button onClick={handleExportAction} className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          )}
        </div>
      </div>

      {terminalsSummary && (
        <>
          {/* Summary Cards */}
          <div className={`grid grid-cols-${terminalsSummary?.types?.length + 1} gap-4 mb-4`} style={{ gridTemplateColumns: `repeat(${terminalsSummary?.types?.length + 1}, 1fr)` }}>
            {/* All Terminals */}
            <Card key="all">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  All Terminals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <span className="text-green-500">{terminalsSummary?.deployed || 0}</span> / {terminalsSummary?.total || 0}
                </div>
                <p className="text-xs mt-1">Currently deployed</p>
              </CardContent>
            </Card>

            {/* Terminal Types */}
            {terminalsSummary?.types?.sort((a, b) => TERMINAL_TYPE_INFO[a.type].sortOrder - TERMINAL_TYPE_INFO[b.type].sortOrder).map((terminalTypeInfo) => {
              const numberOfTerminals = terminalTypeInfo.total || 0;
              const activeTerminals = terminalTypeInfo.deployed || 0;
              const terminalTypeName = TERMINAL_TYPE_INFO[terminalTypeInfo.type].value;
              const terminalTypeColor = getTerminalTypeTextColorClass(terminalTypeInfo.type);
              return (
                <Card key={terminalTypeInfo.type}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-md font-medium flex items-center gap-2">
                      {terminalTypeName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold"><span className={terminalTypeColor}>{activeTerminals}</span> / {numberOfTerminals}</div>
                    <p className="text-xs mt-1">Currently deployed</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Search and Filters */}
      <div className="glass-panel flex items-center gap-4 justify-between p-4 rounded-lg">
        {/* Search */}
        <div className="relative w-full max-w-1/2">
          <SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search terminals by ID, kit serial, dish serial, notes, service line name and number..." />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 min-w-64">
          <Select value={terminalTypeFilter} onValueChange={setTerminalTypeFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Terminal Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.values(TerminalType).map((type) => (
                <SelectItem key={type} value={type}>
                  {TERMINAL_TYPE_INFO[type].value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" onClick={() => {
          setTerminalTypeFilter('all');
          setSearchInput('');
          setSearchQuery('');
        }}>
          <X className="h-4 w-4" />
          Clear Filters
        </Button>
      </div>

      {/* Terminals Table */}
      {isLoading || (isFetching && !isFetchingNextPage) ? (
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading terminals...</p>
        </div>
      ) : (
        <>
          {error ? (
            <EmptyView
              icon={SatelliteDish}
              title="No Terminals"
              description="An error occurred while fetching terminals."
              actionLabel="Retry"
              onAction={() => handleRefetchTerminals()}
            />
          ) : (terminals?.length > 0 ? (
            <>
              {/* User Terminals Table */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto rounded-lg">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="bg-muted">
                          <TableHead className="text-left py-3 px-4 font-medium font-bold">Terminal</TableHead>
                          <TableHead className="text-left py-3 px-4 font-medium font-bold">Type</TableHead>
                          <TableHead className="text-left py-3 px-4 font-medium font-bold">Notes</TableHead>
                          <TableHead className="text-left py-3 px-4 font-medium font-bold">Service Line</TableHead>
                          {canManageTerminals && <TableHead className="text-left py-3 px-4 font-medium font-bold">Actions</TableHead>}
                          <TableHead className="text-right py-3 px-4 font-medium font-bold"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUserTerminals?.map((terminal) => {
                          const serviceLine = terminal.serviceLine || undefined;
                          const slStatus = serviceLine?.status;
                          const slStatusText = slStatus ? SERVICE_LINE_STATUS_VALUE[slStatus] : '-';
                          const terminalTypeColor = terminal.type ? getTerminalTypeTextColorClass(terminal.type) : '';
                          return (
                            <TableRow
                              key={terminal.userTerminalId}
                              className={`border-b hover:bg-muted/50 ${serviceLine ? 'cursor-pointer' : ''}`}
                              onClick={() => serviceLine ? handleServiceLineClick(serviceLine) : undefined}
                            >
                              <TableCell className="py-3 px-4">
                                <div className="flex flex-col items-start gap-1">
                                  <span className="text-sm font-medium">{terminal.kitSerialNumber}</span>
                                  <div className="flex flex-row items-center gap-1">
                                    <p className="text-xs">ID: <span className="font-medium">{terminal.userTerminalId}</span></p>
                                    <p className="text-xs">Serial: <span className="font-medium">{terminal.dishSerialNumber}</span></p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-4">
                                <div className="flex flex-col items-start gap-1">
                                  {terminal.type ? (
                                    <span className={`text-sm font-medium ${terminalTypeColor}`}>
                                      {TERMINAL_TYPE_INFO[terminal.type].value}
                                    </span>
                                  ) : (
                                    '-'
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-4">
                                <div className="flex flex-col items-start gap-1">
                                  <span className="text-sm font-medium">
                                    {terminal.notes || '-'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-4">
                                {serviceLine ? (
                                  <div className="flex flex-col items-start gap-1">
                                    <div className="flex flex-row items-center gap-1">
                                      <span className="text-sm font-medium">{serviceLine?.serviceLineName || 'Not Set'}</span>
                                      {slStatusText && (
                                        <Badge variant="default" className={getServiceLineStatusBGColorClass(slStatus)}>{slStatusText}</Badge>
                                      )}
                                      {serviceLine?.type ? (
                                        <Badge variant="default" className={getServiceLineTypeBGColorClass(serviceLine.type)}>{SERVICE_LINE_TYPE_VALUES[serviceLine.type]}</Badge>
                                      ) : null}
                                    </div>
                                    <span className="text-xs">{serviceLine?.serviceLineNumber}</span>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              {canManageTerminals &&
                                <TableCell className="py-3 px-4">
                                  <div className="flex flex-row items-center gap-2">
                                    {handleTerminalNotes && (
                                      <Button
                                        variant="outline"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleTerminalNotes(terminal);
                                        }}
                                        className="gap-2">
                                        <Text className="h-4 w-4" />
                                        Notes
                                      </Button>
                                    )}
                                    {handleTransferTerminalToAccount && (terminal.eligibleTargetOrganizations?.length || 0) > 0 && (serviceLine === undefined) && (
                                      // Transfer Button
                                      <Button
                                        variant="outline"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleTransferTerminalToAccount(terminal);
                                        }}
                                        className="gap-2">
                                        <ArrowRightLeft className="h-4 w-4" />
                                        Transfer
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              }
                              <TableCell className="py-3 px-4">
                                <div className="flex justify-end">
                                  {serviceLine && <ChevronRight className="h-6 w-6" />}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-muted">
                          <TableCell
                            colSpan={canManageTerminals ? 6 : 5}
                            className="py-3"
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                                <span className="text-md">
                                  Loaded <span className="font-medium">{terminals.length}</span>
                                  {' '}of <span className="font-bold">{totalCount}</span> terminals
                                </span>
                                {filteredUserTerminals.length !== terminals.length && (
                                  <span className="text-sm">
                                    ({filteredUserTerminals.length} match current search query)
                                  </span>
                                )}
                              </div>
                              {hasNextPage ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="shrink-0 gap-2 self-start sm:self-auto"
                                  disabled={isFetchingNextPage}
                                  onClick={() => void fetchNextPage()}
                                >
                                  {isFetchingNextPage ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      Loading…
                                    </>
                                  ) : (
                                    'Load more'
                                  )}
                                </Button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </CardContent>
                <CardFooter className="p-4">
                  <CardDescription>
                    Showing {filteredUserTerminals?.length} of {terminals?.length} user terminals.
                  </CardDescription>
                </CardFooter>
              </Card>
            </>
          ) : (
            <EmptyView
              icon={SatelliteDish}
              title="No Terminals"
              description="This account doesn't have any terminals to display."
            />
          ))}
        </>
      )}

      {/* Transfer Terminal Dialog */}
      {selectedTerminal?.eligibleTargetOrganizations?.length > 0 &&
        <TransferTerminalToAccountDialog
          open={isTransferTerminalToAccountDialogOpen}
          onOpenChange={setIsTransferTerminalToAccountDialogOpen}
          onSuccess={handleRefetchTerminals}
          terminal={selectedTerminal}
        />
      }

      {/* Terminal Notes Dialog */}
      {selectedTerminal &&
        <TerminalNotesDialog
          open={isTerminalNotesDialogOpen}
          onOpenChange={setIsTerminalNotesDialogOpen}
          onSuccess={handleRefetchTerminals}
          terminal={selectedTerminal}
        />
      }

      {/* Terminals Overview Dialog */}
      {organizationOverviewData && (
        <TerminalsOverviewDialog
          open={isTerminalsOverviewDialogOpen}
          onOpenChange={setIsTerminalsOverviewDialogOpen}
          organizationOverviewData={organizationOverviewData}
        />
      )}
    </div>
  );
}